import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { PlusIcon, CurrencyDollarIcon } from '@heroicons/react/24/outline';
import { formatCurrency } from '../../utils/formatCurrency';
import { downloadReceiptPdf, printReceiptWindow } from '../../utils/receipt';
import { useAuth } from '../../contexts/AuthContext';

const productLabel = (transaction) => {
  if (!transaction.product?.name) return transaction.description || 'Other';
  return `${transaction.product.name}${transaction.product.size ? ` (${transaction.product.size})` : ''}`;
};

const groupTransactionsByShop = (list) => {
  const groups = new Map();
  list.forEach((transaction) => {
    const key = transaction.shop?.name?.trim() || 'Other';
    if (!groups.has(key)) {
      groups.set(key, {
        key,
        title: key,
        subtitle: transaction.shop?.location || '',
        transactions: []
      });
    }
    groups.get(key).transactions.push(transaction);
  });
  return Array.from(groups.values());
};

const groupTransactionsByAgent = (list) => {
  const groups = new Map();
  list.forEach((transaction) => {
    const key = String(transaction.agent?._id || transaction.agent || 'unassigned');
    if (!groups.has(key)) {
      groups.set(key, {
        key,
        title: transaction.agent?.name || 'Depot / unassigned',
        subtitle: transaction.agent?.email || '',
        transactions: []
      });
    }
    groups.get(key).transactions.push(transaction);
  });
  return Array.from(groups.values()).sort((a, b) => a.title.localeCompare(b.title));
};

const groupTransactionsByItem = (list) => {
  const groups = new Map();
  list.forEach((transaction) => {
    const key = String(transaction.product?._id || transaction.product || productLabel(transaction));
    if (!groups.has(key)) {
      groups.set(key, {
        key,
        title: productLabel(transaction),
        subtitle: '',
        transactions: []
      });
    }
    groups.get(key).transactions.push(transaction);
  });
  return Array.from(groups.values());
};

const Transactions = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const isOffice = user?.role === 'admin' || user?.role === 'finance';
  const isFieldAgent = user?.role === 'agent' || user?.role === 'depot_sales';
  const groupByShop = isFieldAgent;
  const groupByAgent = isOffice;
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [availableStock, setAvailableStock] = useState([]);
  const [agents, setAgents] = useState([]);
  const [shops, setShops] = useState([]);
  const [filters, setFilters] = useState({
    search: '',
    type: '',
    returnStatus: '',
    startDate: '',
    endDate: '',
    agent: '',
    shop: ''
  });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(['admin', 'finance'].includes(user?.role) ? 200 : 10);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [showSaleModal, setShowSaleModal] = useState(false);
  const [saleSubmitting, setSaleSubmitting] = useState(false);
  const [saleError, setSaleError] = useState('');
  const [saleData, setSaleData] = useState({
    shop: '',
    shopName: '',
    shopLocation: '',
    isCredit: false,
    notes: ''
  });
  const [showShopModal, setShowShopModal] = useState(false);
  const [shopSubmitting, setShopSubmitting] = useState(false);
  const [shopError, setShopError] = useState('');
  const [shopData, setShopData] = useState({
    name: '',
    location: '',
    phone: '',
    notes: ''
  });
  const [saleItems, setSaleItems] = useState([
    { product: '', quantity: '', unitPrice: '' }
  ]);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editError, setEditError] = useState('');
  const [editData, setEditData] = useState({ id: '', description: '', notes: '' });
  const [summaryData, setSummaryData] = useState({
    totalSales: 0,
    totalSaleAmount: 0,
    totalItems: 0,
    totalCOGS: 0,
    totalExpenses: 0,
    netProfit: 0,
    totalTransactions: 0
  });

  useEffect(() => {
    fetchTransactions();
  }, [page, limit, filters]);

  useEffect(() => {
    fetchSummary();
  }, [filters]);

  useEffect(() => {
    fetchShops();
    if (isFieldAgent) {
      fetchAvailableStock();
    }
    if (user?.role === 'admin' || user?.role === 'finance') {
      fetchAgents();
    }
  }, [user?.role]);

  const fetchTransactions = async () => {
    try {
      const response = await axios.get('/api/transactions', {
        params: {
          page,
          limit,
          search: filters.search || undefined,
          shop: filters.shop || undefined,
          type: filters.type || undefined,
          returnStatus: filters.returnStatus || undefined,
          startDate: filters.startDate || undefined,
          endDate: filters.endDate || undefined,
          agent: filters.agent || undefined
        }
      });
      setTransactions(response.data.transactions || []);
      setTotalPages(response.data.totalPages || 1);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const response = await axios.get('/api/transactions/summary', {
        params: {
          startDate: filters.startDate || undefined,
          endDate: filters.endDate || undefined,
          agent: filters.agent || undefined,
          shop: filters.shop || undefined
        }
      });
      const summary = response.data.summary || [];
      const totalTransactions = summary.reduce((sum, item) => sum + (item.count || 0), 0);
      setSummaryData({
        totalSales: response.data.totalSales || 0,
        totalSaleAmount: response.data.totalSaleAmount || 0,
        totalItems: response.data.totalSaleItems || 0,
        totalCOGS: response.data.totalCOGS || 0,
        totalExpenses: response.data.totalExpenses || 0,
        netProfit: response.data.netProfit || 0,
        totalTransactions
      });
    } catch (error) {
      console.error('Error fetching transaction summary:', error);
      setSummaryData({
        totalSales: 0,
        totalSaleAmount: 0,
        totalItems: 0,
        totalCOGS: 0,
        totalExpenses: 0,
        netProfit: 0,
        totalTransactions: 0
      });
    }
  };

  const fetchAgents = async () => {
    try {
      const response = await axios.get('/api/users/agents');
      setAgents(response.data || []);
    } catch (error) {
      console.error('Error fetching agents:', error);
    }
  };

  const fetchShops = async () => {
    try {
      const response = await axios.get('/api/shops');
      setShops(response.data.shops || []);
    } catch (error) {
      console.error('Error fetching shops:', error);
    }
  };

  const fetchAvailableStock = async () => {
    try {
      const response = await axios.get('/api/stock', {
        params: { page: 1, limit: 1000, all: true }
      });
      const byProduct = new Map();
      (response.data.stock || []).forEach((item) => {
        const product = item.product;
        if (!product?._id) return;
        const qty = Number(item.quantity) || 0;
        if (!byProduct.has(product._id)) {
          byProduct.set(product._id, {
            _id: product._id,
            name: product.name,
            size: product.size,
            unitPrice: product.unitPrice,
            available: 0
          });
        }
        byProduct.get(product._id).available += qty;
      });
      setAvailableStock(Array.from(byProduct.values()).filter((item) => item.available > 0));
    } catch (error) {
      console.error('Error fetching available stock:', error);
      setAvailableStock([]);
    }
  };

  const groupedTransactions = useMemo(() => {
    if (groupByShop) return groupTransactionsByShop(transactions);
    if (groupByAgent) return groupTransactionsByAgent(transactions);
    return groupTransactionsByItem(transactions);
  }, [groupByAgent, groupByShop, transactions]);

  const saleTotals = useMemo(
    () =>
      saleItems.reduce(
        (acc, item) => {
          const qty = Number(item.quantity) || 0;
          const price = Number(item.unitPrice) || 0;
          acc.items += qty;
          acc.amount += qty * price;
          return acc;
        },
        { items: 0, amount: 0 }
      ),
    [saleItems]
  );

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
    setPage(1);
  };

  const openShopModal = () => {
    setShopError('');
    setShopData({ name: '', location: '', phone: '', notes: '' });
    setShowShopModal(true);
  };

  const closeShopModal = () => {
    setShowShopModal(false);
    setShopData({ name: '', location: '', phone: '', notes: '' });
  };

  const handleShopChange = (event) => {
    const { name, value } = event.target;
    setShopData((prev) => ({ ...prev, [name]: value }));
  };

  const handleShopSubmit = async (event) => {
    event.preventDefault();
    setShopSubmitting(true);
    setShopError('');

    try {
      await axios.post('/api/shops', shopData);
      await fetchShops();
      closeShopModal();
    } catch (error) {
      console.error('Error creating shop:', error);
      setShopError(
        error.response?.data?.message || 'Failed to create shop. Try again.'
      );
    } finally {
      setShopSubmitting(false);
    }
  };

  const handleShopSelect = (event) => {
    const shopId = event.target.value;
    const selectedShop = shops.find(s => s._id === shopId);
    
    if (selectedShop) {
      setSaleData((prev) => ({
        ...prev,
        shop: shopId,
        shopName: selectedShop.name,
        shopLocation: selectedShop.location
      }));
    } else {
      setSaleData((prev) => ({
        ...prev,
        shop: '',
        shopName: '',
        shopLocation: ''
      }));
    }
  };

  const openSaleModal = () => {
    setSaleError('');
    fetchAvailableStock();
    setShowSaleModal(true);
  };

  const closeSaleModal = () => {
    setShowSaleModal(false);
  };

  const handleSaleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setSaleData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleItemChange = (index, field, value) => {
    setSaleItems((prev) =>
      prev.map((item, idx) => {
        if (idx !== index) return item;
        const next = { ...item, [field]: value };
        if (field === 'product') {
          const stockItem = availableStock.find((product) => product._id === value);
          if (stockItem?.unitPrice != null && stockItem.unitPrice !== '') {
            next.unitPrice = stockItem.unitPrice;
          }
        }
        return next;
      })
    );
  };

  const addItem = () => {
    setSaleItems((prev) => [...prev, { product: '', quantity: '', unitPrice: '' }]);
  };

  const removeItem = (index) => {
    setSaleItems((prev) => prev.filter((_, idx) => idx !== index));
  };

  const handleSaleSubmit = async (event) => {
    event.preventDefault();
    setSaleSubmitting(true);
    setSaleError('');

    try {
      for (const item of saleItems) {
        const stockItem = availableStock.find((product) => product._id === item.product);
        if (!stockItem) {
          setSaleError('Select a product that is in stock.');
          setSaleSubmitting(false);
          return;
        }
        if (Number(item.quantity) > stockItem.available) {
          setSaleError(`${stockItem.name} only has ${stockItem.available} available.`);
          setSaleSubmitting(false);
          return;
        }
      }

      const response = await axios.post('/api/transactions/sale', {
        items: saleItems.map((item) => ({
          product: item.product,
          quantity: Number(item.quantity),
          unitPrice: Number(item.unitPrice)
        })),
        shop: {
          name: saleData.shopName,
          location: saleData.shopLocation
        },
        isCredit: saleData.isCredit,
        notes: saleData.notes
      });

      console.log('Sale recorded successfully:', response.data);

      setSaleData({
        shop: '',
        shopName: '',
        shopLocation: '',
        isCredit: false,
        notes: ''
      });
      setSaleItems([{ product: '', quantity: '', unitPrice: '' }]);
      setShowSaleModal(false);
      
      // Reset filters to today to show the new transaction
      const today = new Date().toISOString().split('T')[0];
      console.log('Setting filters to today:', today);
      setFilters(prev => ({
        ...prev,
        startDate: today,
        endDate: today
      }));
      setPage(1);
      
      // Wait a moment for state to update, then fetch
      setTimeout(async () => {
        await fetchTransactions();
        await fetchSummary();
        await fetchAvailableStock();
        console.log('Transactions refreshed');
      }, 100);
    } catch (error) {
      console.error('Error recording sale:', error);
      setSaleError(
        error.response?.data?.message || 'Failed to record sale. Try again.'
      );
    } finally {
      setSaleSubmitting(false);
    }
  };

  const summary = summaryData;

  const getReceiptTransactions = async (transaction) => {
    if (!transaction.receiptGroup) {
      return [transaction];
    }

    const response = await axios.get(`/api/transactions/receipt/${transaction.receiptGroup}`);
    return response.data.transactions || [transaction];
  };

  const buildReceiptData = (transactionsForReceipt) => {
    const first = transactionsForReceipt[0];
    const receiptId = first.receiptGroup || first._id;
    const metaLines = [
      `Receipt ID: ${receiptId}`,
      `Date: ${new Date(first.createdAt).toLocaleString()}`,
      `Shop: ${first.shop?.name || ''} ${first.shop?.location ? `(${first.shop.location})` : ''}`
    ];

    const columns = ['Product', 'Quantity', 'Unit Price', 'Total'];
    const rows = transactionsForReceipt.map((item) => [
      `${item.product?.name || ''}${item.product?.size ? ` (${item.product.size})` : ''}`,
      item.quantity,
      formatCurrency(item.unitPrice),
      formatCurrency(item.amount)
    ]);
    const totalAmount = transactionsForReceipt.reduce((sum, item) => sum + (item.amount || 0), 0);
    rows.push(['Total', '', '', formatCurrency(totalAmount)]);

    return { metaLines, columns, rows, receiptId };
  };

  const handleReceiptAction = async (transaction, action) => {
    try {
      const transactionsForReceipt = await getReceiptTransactions(transaction);
      const { metaLines, columns, rows, receiptId } = buildReceiptData(transactionsForReceipt);

      if (action === 'print') {
        printReceiptWindow({
          title: 'Sale Receipt',
          metaLines,
          columns,
          rows
        });
      } else {
        downloadReceiptPdf({
          title: 'Sale Receipt',
          metaLines,
          columns,
          rows,
          filename: `sale-receipt-${receiptId}.pdf`
        });
      }
    } catch (receiptError) {
      console.error('Error generating receipt:', receiptError);
    }
  };

  const openEditModal = (transaction) => {
    setEditError('');
    setEditData({
      id: transaction._id,
      description: transaction.description || '',
      notes: transaction.notes || ''
    });
    setEditModalOpen(true);
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setEditData({ id: '', description: '', notes: '' });
  };

  const handleEditChange = (event) => {
    const { name, value } = event.target;
    setEditData((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditSubmit = async (event) => {
    event.preventDefault();
    setEditSubmitting(true);
    setEditError('');
    try {
      await axios.put(`/api/transactions/${editData.id}`, {
        description: editData.description,
        notes: editData.notes
      });
      closeEditModal();
      fetchTransactions();
    } catch (editErr) {
      console.error('Error updating transaction:', editErr);
      setEditError(editErr.response?.data?.message || 'Failed to update transaction.');
    } finally {
      setEditSubmitting(false);
    }
  };

  const handleDelete = async (transaction) => {
    if (!window.confirm('Delete this return record?')) return;
    try {
      await axios.delete(`/api/transactions/${transaction._id}`);
      fetchTransactions();
    } catch (deleteErr) {
      console.error('Error deleting transaction:', deleteErr);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center rounded-lg border border-secondary-200 bg-white px-6 py-4">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">Transactions</h1>
          <p className="mt-1 text-sm text-secondary-600">
            {groupByShop
              ? 'Sales and payments grouped by shop.'
              : groupByAgent
              ? 'Transactions grouped by agent, showing what was distributed to each of them.'
              : 'Transactions grouped by product item.'}
          </p>
        </div>
        {(user?.role === 'agent' || user?.role === 'depot_sales') && (
          <div className="flex gap-3">
            <button className="btn-secondary flex items-center gap-2" onClick={openShopModal}>
              <PlusIcon className="h-5 w-5" />
              Add Shop
            </button>
            <button className="btn-primary flex items-center gap-2" onClick={openSaleModal}>
              <PlusIcon className="h-5 w-5" />
              Record Sale
            </button>
          </div>
        )}
      </div>

      {/* Transaction Summary (Admin only) */}
      {isAdmin && (
        <>
          <div className="card bg-secondary-50">
            <div className="card-body">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-secondary-900">Financial Summary</h3>
                <p className="text-sm text-secondary-600">
                  {filters.startDate && filters.endDate
                    ? `${new Date(filters.startDate).toLocaleDateString()} - ${new Date(filters.endDate).toLocaleDateString()}`
                    : filters.startDate
                    ? `From ${new Date(filters.startDate).toLocaleDateString()}`
                    : filters.endDate
                    ? `Until ${new Date(filters.endDate).toLocaleDateString()}`
                    : 'All Time'}
                </p>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="card bg-secondary-50">
              <div className="card-body">
                <div className="flex items-center">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-success-100">
                    <CurrencyDollarIcon className="h-6 w-6 text-success-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-secondary-600">Total Sales</p>
                    <p className="text-2xl font-semibold text-secondary-900">{formatCurrency(summary.totalSales)}</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="card bg-secondary-50">
              <div className="card-body">
                <div className="flex items-center">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-orange-100">
                    <CurrencyDollarIcon className="h-6 w-6 text-orange-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-secondary-600">Cost of Goods</p>
                    <p className="text-2xl font-semibold text-secondary-900">{formatCurrency(summary.totalCOGS)}</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="card bg-secondary-50">
              <div className="card-body">
                <div className="flex items-center">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-red-100">
                    <CurrencyDollarIcon className="h-6 w-6 text-red-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-secondary-600">Total Expenses</p>
                    <p className="text-2xl font-semibold text-secondary-900">{formatCurrency(summary.totalExpenses)}</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="card bg-secondary-50">
              <div className="card-body">
                <div className="flex items-center">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-100">
                    <CurrencyDollarIcon className="h-6 w-6 text-primary-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-secondary-600">Net Profit</p>
                    <p className="text-2xl font-semibold text-secondary-900">{formatCurrency(summary.netProfit)}</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="card bg-secondary-50">
              <div className="card-body">
                <div className="flex items-center">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-secondary-100">
                    <CurrencyDollarIcon className="h-6 w-6 text-secondary-700" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-secondary-600">Transactions</p>
                    <p className="text-2xl font-semibold text-secondary-900">{summary.totalTransactions}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {isFieldAgent && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="card bg-secondary-50">
            <div className="card-body">
              <div className="flex items-center">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-100">
                  <CurrencyDollarIcon className="h-6 w-6 text-primary-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-secondary-600">Total Items</p>
                  <p className="text-2xl font-semibold text-secondary-900">{summary.totalItems || 0}</p>
                </div>
              </div>
            </div>
          </div>
          <div className="card bg-secondary-50">
            <div className="card-body">
              <div className="flex items-center">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-success-100">
                  <CurrencyDollarIcon className="h-6 w-6 text-success-600" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-secondary-600">Total Amount</p>
                  <p className="text-2xl font-semibold text-secondary-900">
                    {formatCurrency(summary.totalSaleAmount || 0)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="card bg-secondary-50">
        <div className="card-body">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-6">
            <input
              type="text"
              name="search"
              value={filters.search}
              onChange={handleFilterChange}
              placeholder="Search by shop..."
              className="input-field md:col-span-2"
            />
            <select
              name="shop"
              value={filters.shop}
              onChange={handleFilterChange}
              className="input-field"
            >
              <option value="">All shops</option>
              {shops.map((shop) => (
                <option key={shop._id} value={shop.name}>
                  {shop.name}{shop.location ? ` - ${shop.location}` : ''}
                </option>
              ))}
            </select>
            <select
              name="type"
              value={filters.type}
              onChange={handleFilterChange}
              className="input-field"
            >
              <option value="">All types</option>
              <option value="sale">Sale</option>
              <option value="payment">Payment</option>
              <option value="return">Return</option>
              <option value="stock_addition">Stock Addition</option>
              <option value="stock_transfer">Stock Transfer</option>
              <option value="stock_damage">Stock Damage</option>
              <option value="factory_credit">Factory Credit</option>
              <option value="agent_return">Agent Return</option>
              <option value="agent_settlement">Agent Settlement</option>
              <option value="expense">Expense</option>
              <option value="purchase">Purchase</option>
            </select>
            <select
              name="returnStatus"
              value={filters.returnStatus}
              onChange={handleFilterChange}
              className="input-field"
            >
              <option value="">All return status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
            {(user?.role === 'admin' || user?.role === 'finance') && (
              <select
                name="agent"
                value={filters.agent}
                onChange={handleFilterChange}
                className="input-field"
              >
                <option value="">All agents</option>
                {agents.map((agent) => (
                  <option key={agent._id} value={agent._id}>
                    {agent.name} ({agent.email})
                  </option>
                ))}
              </select>
            )}
            <input
              type="date"
              name="startDate"
              value={filters.startDate}
              onChange={handleFilterChange}
              className="input-field"
            />
            <input
              type="date"
              name="endDate"
              value={filters.endDate}
              onChange={handleFilterChange}
              className="input-field"
            />
          </div>
        </div>
      </div>

      {/* Transactions grouped by shop (agents) or product (admin) */}
      <div className="space-y-4">
        {groupedTransactions.length === 0 ? (
          <div className="card bg-secondary-50">
            <p className="px-6 py-10 text-center text-sm text-secondary-500">No transactions yet.</p>
          </div>
        ) : (
          groupedTransactions.map((group) => {
            const groupTotal = group.transactions.reduce((sum, item) => sum + (item.amount || 0), 0);
            const groupQty = group.transactions.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
            return (
              <div key={group.key} className="card bg-secondary-50 overflow-hidden">
                <div className="flex items-center justify-between border-b border-secondary-200 bg-secondary-100 px-6 py-3">
                  <div>
                    <h3 className="text-sm font-semibold text-secondary-900">{group.title}</h3>
                    {group.subtitle ? (
                      <p className="text-xs text-secondary-500">{group.subtitle}</p>
                    ) : null}
                  </div>
                  <p className="text-sm text-secondary-600">
                    {group.transactions.length} · Qty {groupQty} · {formatCurrency(groupTotal)}
                  </p>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-secondary-200">
                    <thead className="bg-white">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-secondary-600 uppercase tracking-wider">
                          Date
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-secondary-600 uppercase tracking-wider">
                          Type
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-secondary-600 uppercase tracking-wider">
                          {groupByShop ? 'Item' : groupByAgent ? 'Distributed item' : 'Shop / details'}
                        </th>
                        {groupByAgent && (
                          <th className="px-6 py-3 text-left text-xs font-medium text-secondary-600 uppercase tracking-wider">
                            Qty
                          </th>
                        )}
                        <th className="px-6 py-3 text-left text-xs font-medium text-secondary-600 uppercase tracking-wider">
                          Amount
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-secondary-600 uppercase tracking-wider">
                          Status
                        </th>
                        {isFieldAgent && (
                          <th className="px-6 py-3 text-left text-xs font-medium text-secondary-600 uppercase tracking-wider">
                            Receipt
                          </th>
                        )}
                        {isAdmin && (
                          <th className="px-6 py-3 text-left text-xs font-medium text-secondary-600 uppercase tracking-wider">
                            Actions
                          </th>
                        )}
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-secondary-100">
                      {group.transactions.map((transaction) => (
                        <tr key={transaction._id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-900">
                            {new Date(transaction.createdAt).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              transaction.type === 'sale'
                                ? 'bg-green-100 text-green-800'
                                : transaction.type === 'stock_transfer'
                                ? 'bg-primary-100 text-primary-800'
                                : transaction.type === 'expense'
                                ? 'bg-red-100 text-red-800'
                                : 'bg-blue-100 text-blue-800'
                            }`}>
                              {transaction.type === 'stock_transfer' ? 'distributed' : transaction.type}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-900">
                            {groupByAgent
                              ? (
                                <>
                                  {productLabel(transaction)}
                                  {transaction.shop?.name ? (
                                    <div className="text-xs text-secondary-500">
                                      {transaction.shop.name}
                                      {transaction.shop.location ? ` (${transaction.shop.location})` : ''}
                                    </div>
                                  ) : null}
                                </>
                              )
                              : groupByShop
                              ? productLabel(transaction)
                              : (transaction.shop?.name
                                ? `${transaction.shop.name}${transaction.shop.location ? ` (${transaction.shop.location})` : ''}`
                                : transaction.description)}
                            {['return', 'agent_return'].includes(transaction.type) && (
                              <div className="text-xs text-secondary-500">
                                Qty {transaction.quantity || 0}
                                {transaction.type === 'return' && transaction.returnReviewReason
                                  ? ` · ${transaction.returnReviewReason}`
                                  : transaction.type === 'agent_return'
                                  ? ' · Unsold'
                                  : ''}
                              </div>
                            )}
                          </td>
                          {groupByAgent && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-900">
                              {transaction.quantity != null ? transaction.quantity : '—'}
                            </td>
                          )}
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-900">
                            {formatCurrency(transaction.amount)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                              Completed
                            </span>
                          </td>
                          {isFieldAgent && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-900">
                              {transaction.type === 'sale' ? (
                                <div className="flex gap-2">
                                  <button
                                    className="text-primary-600 hover:text-primary-700 font-medium"
                                    onClick={() => handleReceiptAction(transaction, 'print')}
                                  >
                                    Print
                                  </button>
                                  <button
                                    className="text-secondary-600 hover:text-secondary-800 font-medium"
                                    onClick={() => handleReceiptAction(transaction, 'pdf')}
                                  >
                                    PDF
                                  </button>
                                </div>
                              ) : (
                                <span className="text-xs text-secondary-400">—</span>
                              )}
                            </td>
                          )}
                          {isAdmin && (
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-900">
                              <div className="flex gap-3">
                                <button
                                  className="text-primary-600 hover:text-primary-700 font-medium"
                                  onClick={() => openEditModal(transaction)}
                                >
                                  Edit
                                </button>
                                {['return', 'agent_return'].includes(transaction.type) && (
                                  <button
                                    className="text-red-600 hover:text-red-700 font-medium"
                                    onClick={() => handleDelete(transaction)}
                                  >
                                    Delete
                                  </button>
                                )}
                              </div>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })
        )}
        <div className="flex flex-col items-center justify-between gap-3 rounded-lg border border-secondary-200 bg-white px-6 py-4 text-sm text-secondary-600 md:flex-row">
          <div>
            Showing page {page} of {totalPages} · {total} items
          </div>
          <div className="flex items-center gap-2">
            <select
              value={limit}
              onChange={(event) => {
                setLimit(Number(event.target.value));
                setPage(1);
              }}
              className="input-field h-9"
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={200}>200</option>
              <option value={1000}>All</option>
            </select>
            <button
              className="btn-secondary"
              onClick={() => setPage((prev) => Math.max(1, prev - 1))}
              disabled={page <= 1}
            >
              Prev
            </button>
            <button
              className="btn-secondary"
              onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
              disabled={page >= totalPages}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {showSaleModal && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-40 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border border-secondary-200 max-w-4xl w-full shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-secondary-900 mb-4">Record Sale</h3>
              <form onSubmit={handleSaleSubmit} className="space-y-4">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <label className="block text-sm font-medium text-secondary-700">Items</label>
                    <button type="button" className="text-primary-600 text-sm" onClick={addItem}>
                      Add item
                    </button>
                  </div>
                  {saleItems.map((item, index) => {
                    const selectedStock = availableStock.find((product) => product._id === item.product);
                    const availableQty = selectedStock?.available || 0;
                    return (
                    <div key={index} className="grid grid-cols-1 gap-3 md:grid-cols-[2fr_1fr_1fr_auto]">
                      <select
                        value={item.product}
                        onChange={(event) => handleItemChange(index, 'product', event.target.value)}
                        className="input-field"
                        required
                      >
                        <option value="">Select product in stock</option>
                        {availableStock.map((product) => (
                          <option key={product._id} value={product._id}>
                            {product.name}{product.size ? ` (${product.size})` : ''} — {product.available} available
                          </option>
                        ))}
                      </select>
                      <input
                        type="number"
                        value={item.quantity}
                        onChange={(event) => handleItemChange(index, 'quantity', event.target.value)}
                        className="input-field"
                        min="1"
                        max={availableQty || undefined}
                        placeholder={availableQty ? `Qty (max ${availableQty})` : 'Qty'}
                        required
                      />
                      <input
                        type="number"
                        value={item.unitPrice}
                        onChange={(event) => handleItemChange(index, 'unitPrice', event.target.value)}
                        className="input-field"
                        min="0"
                        step="0.01"
                        placeholder="Unit price"
                        required
                      />
                      {saleItems.length > 1 && (
                        <button
                          type="button"
                          className="text-red-600 hover:text-red-700 font-medium whitespace-nowrap px-2"
                          onClick={() => removeItem(index)}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                    );
                  })}
                  {availableStock.length === 0 && (
                    <p className="text-sm text-secondary-500">No products with available quantity. Check stock first.</p>
                  )}
                  <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-secondary-200 bg-secondary-50 px-4 py-3">
                    <div>
                      <p className="text-xs font-medium uppercase text-secondary-600">Total items</p>
                      <p className="text-xl font-semibold text-secondary-900">{saleTotals.items}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs font-medium uppercase text-secondary-600">Total amount</p>
                      <p className="text-xl font-semibold text-secondary-900">{formatCurrency(saleTotals.amount)}</p>
                    </div>
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-secondary-700">Select Shop</label>
                    <button
                      type="button"
                      className="text-primary-600 text-sm font-medium hover:text-primary-700"
                      onClick={openShopModal}
                    >
                      + Add New Shop
                    </button>
                  </div>
                  <select
                    name="shop"
                    value={saleData.shop}
                    onChange={handleShopSelect}
                    className="input-field"
                    required
                  >
                    <option value="">Select a shop</option>
                    {shops.map((shop) => (
                      <option key={shop._id} value={shop._id}>
                        {shop.name} - {shop.location}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    id="isCredit"
                    type="checkbox"
                    name="isCredit"
                    checked={saleData.isCredit}
                    onChange={handleSaleChange}
                    className="h-4 w-4 text-primary-600"
                  />
                  <label htmlFor="isCredit" className="text-sm text-secondary-700">
                    Credit sale (shop pays later)
                  </label>
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700">Notes</label>
                  <textarea
                    name="notes"
                    value={saleData.notes}
                    onChange={handleSaleChange}
                    className="input-field"
                    rows="3"
                  />
                </div>

                {saleError && (
                  <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">
                    {saleError}
                  </div>
                )}

                <div className="flex justify-end gap-3">
                  <button type="button" className="btn-secondary" onClick={closeSaleModal}>
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary" disabled={saleSubmitting}>
                    {saleSubmitting ? 'Saving...' : 'Save Sale'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {editModalOpen && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-40 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border border-secondary-200 w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-secondary-900 mb-4">Edit Transaction</h3>
              <form onSubmit={handleEditSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-secondary-700">Description</label>
                  <input
                    type="text"
                    name="description"
                    value={editData.description}
                    onChange={handleEditChange}
                    className="input-field"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700">Notes</label>
                  <textarea
                    name="notes"
                    value={editData.notes}
                    onChange={handleEditChange}
                    className="input-field"
                    rows="3"
                  />
                </div>
                {editError && (
                  <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">
                    {editError}
                  </div>
                )}
                <div className="flex justify-end gap-3">
                  <button type="button" className="btn-secondary" onClick={closeEditModal}>
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary" disabled={editSubmitting}>
                    {editSubmitting ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {showShopModal && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-40 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border border-secondary-200 w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-secondary-900 mb-4">Add New Shop</h3>
              <form onSubmit={handleShopSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-secondary-700">Shop Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={shopData.name}
                    onChange={handleShopChange}
                    className="input-field"
                    placeholder="e.g. Mama Njeri Shop"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700">Location *</label>
                  <input
                    type="text"
                    name="location"
                    value={shopData.location}
                    onChange={handleShopChange}
                    className="input-field"
                    placeholder="e.g. Westlands, Nairobi"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700">Phone Number</label>
                  <input
                    type="tel"
                    name="phone"
                    value={shopData.phone}
                    onChange={handleShopChange}
                    className="input-field"
                    placeholder="e.g. +254700000000"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700">Notes</label>
                  <textarea
                    name="notes"
                    value={shopData.notes}
                    onChange={handleShopChange}
                    className="input-field"
                    rows="2"
                    placeholder="Any additional information..."
                  />
                </div>
                {shopError && (
                  <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">
                    {shopError}
                  </div>
                )}
                <div className="flex justify-end gap-3">
                  <button type="button" className="btn-secondary" onClick={closeShopModal}>
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary" disabled={shopSubmitting}>
                    {shopSubmitting ? 'Adding...' : 'Add Shop'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Transactions;
